import { Target, Trophy, Zap, Heart } from 'lucide-react';
import { getProgressColors } from '@/utils/goal-progress-colors';

export interface EncouragementMessage {
  message: string;
  subMessage: string;
  icon: React.ComponentType<any>;
  showSparkles: boolean;
  showCelebration: boolean;
  progressGradient: string;
  cardColor: string;
  cardBg: string;
  cardBorder: string;
  cardGradient: string;
  shadowClass: string;
  hoverColor: string;
}

/**
 * Generate dynamic encouragement messages based on progress
 * @param progress - Progress percentage (0-100)
 * @param totalTasks - Total number of tasks
 * @param completedTasksCount - Number of completed tasks (for message variation)
 * @returns EncouragementMessage object with message, styling, and effects
 */
export const useEncouragement = (progress: number, totalTasks: number, completedTasksCount: number = 0): EncouragementMessage => {
  // Use a combination of progress and completed tasks to create variety
  const messageIndex = (completedTasksCount + Math.floor(progress / 10)) % 3;
  
  const colors = getProgressColors(progress);
  
  if (progress === 100) {
    const messages = [
      {
        message: "🎉 Incredible! You've completed your goal!",
        subMessage: "Time to celebrate your amazing achievement!"
      },
      {
        message: "🏆 Victory achieved! You're unstoppable!",
        subMessage: "What an incredible accomplishment - you should be proud!"
      },
      {
        message: "✨ Goal conquered! You're a champion!",
        subMessage: "This is what dedication and hard work look like!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Trophy,
      showSparkles: true,
      showCelebration: true
    };
  } else if (progress >= 80) {
    const messages = [
      {
        message: "🔥 You're almost there! The finish line is in sight!",
        subMessage: "Just a few more tasks to reach your goal!"
      },
      {
        message: "⚡ So close to victory! Push through!",
        subMessage: "You're in the final stretch - keep that momentum!"
      },
      {
        message: "🚀 Nearly there! You're crushing it!",
        subMessage: "The end is near and success awaits you!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Zap,
      showSparkles: true,
      showCelebration: false
    };
  } else if (progress >= 60) {
    const messages = [
      {
        message: "💪 Great momentum! You're making excellent progress!",
        subMessage: "Keep up the fantastic work!"
      },
      {
        message: "🌟 You're on fire! Outstanding progress!",
        subMessage: "Your dedication is really showing!"
      },
      {
        message: "⭐ Fantastic work! You're more than halfway there!",
        subMessage: "Your consistency is paying off beautifully!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Zap,
      showSparkles: false,
      showCelebration: false
    };
  } else if (progress >= 40) {
    const messages = [
      {
        message: "🚀 You're building great momentum!",
        subMessage: "Every task completed brings you closer to success!"
      },
      {
        message: "💫 Excellent progress! You're really getting there!",
        subMessage: "Keep this energy up - you're doing amazing!"
      },
      {
        message: "🎯 Solid progress! You're making it happen!",
        subMessage: "Each step forward is a step toward victory!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Target,
      showSparkles: false,
      showCelebration: false
    };
  } else if (progress >= 20) {
    const messages = [
      {
        message: "✨ You've made a great start!",
        subMessage: "Keep going - every step counts!"
      },
      {
        message: "🌱 Nice beginning! You're off to a wonderful start!",
        subMessage: "Building habits one task at a time!"
      },
      {
        message: "💖 Great first steps! You're on your way!",
        subMessage: "Progress is progress - you're doing great!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Heart,
      showSparkles: false,
      showCelebration: false
    };
  } else if (totalTasks > 0) {
    const messages = [
      {
        message: "🌟 Every journey begins with a single step!",
        subMessage: "You've got this - let's make progress together!"
      },
      {
        message: "🎯 Ready to make things happen!",
        subMessage: "Your goal is waiting - let's tackle those tasks!"
      },
      {
        message: "💪 Time to show what you're made of!",
        subMessage: "Every expert was once a beginner - you've got this!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Target,
      showSparkles: false,
      showCelebration: false
    };
  } else {
    const messages = [
      {
        message: "🎯 Ready to achieve something amazing?",
        subMessage: "Add some tasks to get started on your goal!"
      },
      {
        message: "✨ Your journey to success starts here!",
        subMessage: "Create your first task and begin the adventure!"
      },
      {
        message: "🚀 Let's turn dreams into reality!",
        subMessage: "Every great achievement starts with a plan!"
      }
    ];
    return {
      ...messages[messageIndex],
      ...colors,
      icon: Target,
      showSparkles: false,
      showCelebration: false
    };
  }
};

/**
 * Get encouragement message for the highest progress goal
 * @param activeGoals - Array of active goals
 * @returns EncouragementMessage for the goal with highest progress, or default message
 */
export const useTopGoalEncouragement = (activeGoals: any[]): EncouragementMessage => {
  if (!activeGoals || activeGoals.length === 0) {
    // Default encouraging message when no active goals
    const colors = getProgressColors(0);
    return {
      message: "🌟 Ready to start your first goal!",
      subMessage: "Every great achievement begins with a single step forward!",
      icon: Target,
      showSparkles: false,
      showCelebration: false,
      ...colors
    };
  }

  // Find the goal with the highest progress
  const topGoal = activeGoals.reduce((highest, current) => {
    const currentProgress = current.progress || 0;
    const highestProgress = highest.progress || 0;
    return currentProgress > highestProgress ? current : highest;
  }, activeGoals[0]);

  // Use the encouragement logic for the top goal
  return useEncouragement(topGoal.progress || 0, 0, 0);
}; 